import { useEffect, useRef, useState } from "react";
import { testimonials } from "../../../mocks/testimonials";

export default function TestimonialsSection() {
  const [visible, setVisible] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const visibleTestimonials = showAll ? testimonials : testimonials.slice(0, 3);

  return (
    <section id="testimonials" ref={ref} className="bg-charcoal-50 py-10 md:py-24 lg:py-28 px-4 md:px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div
          className={`text-center mb-8 md:mb-16 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="text-forest-600 text-xs font-semibold tracking-[0.25em] uppercase">
            Client Testimonials
          </span>
          <h2 className="font-cormorant text-2xl md:text-4xl lg:text-5xl font-semibold text-charcoal-900 mt-2 md:mt-3 mb-3 md:mb-4">
            Trusted by Hundreds of Clients
          </h2>
          <p className="text-charcoal-500 text-xs md:text-base max-w-xl mx-auto leading-relaxed">
            From small businesses to established corporations and individual professionals —
            here&apos;s what our clients say about working with us.
          </p>
        </div>

        {/* Grid — shows 3 on mobile, all on tablet+ */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5">
          {/* On mobile: show only 3 or all depending on toggle */}
          {visibleTestimonials.map((t, i) => (
            <div
              key={t.id}
              className={`bg-white rounded-lg p-4 md:p-7 border border-charcoal-100 hover:border-forest-300 hover:ring-1 hover:ring-forest-100 transition-all duration-500 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              } ${i >= 3 ? "hidden md:block" : ""}`}
              style={{ transitionDelay: `${i * 80 + 200}ms` }}
            >
              {/* Stars */}
              <div className="flex items-center gap-0.5 mb-2.5 md:mb-4">
                {[...Array(5)].map((_, idx) => (
                  <i key={idx} className="ri-star-fill text-forest-500 text-xs" />
                ))}
              </div>

              <p className="text-charcoal-600 text-xs md:text-sm leading-relaxed mb-4 md:mb-6">
                &ldquo;{t.text}&rdquo;
              </p>

              {/* Author */}
              <div className="flex items-center gap-3 pt-3 md:pt-4 border-t border-charcoal-100">
                <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-forest-600 text-white text-xs font-semibold flex-shrink-0">
                  {t.initials}
                </div>
                <div>
                  <p className="text-charcoal-900 text-xs md:text-sm font-semibold">{t.name}</p>
                  <p className="text-charcoal-400 text-xs mt-0.5">
                    {t.role}
                    {t.company !== "Independent" && ` · ${t.company}`}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Mobile-only extra cards when showAll */}
          {showAll && testimonials.slice(3).map((t, i) => (
            <div
              key={`extra-${t.id}`}
              className={`bg-white rounded-lg p-4 md:p-7 border border-charcoal-100 hover:border-forest-300 hover:ring-1 hover:ring-forest-100 transition-all duration-500 md:hidden ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${(i + 3) * 80 + 200}ms` }}
            >
              <div className="flex items-center gap-0.5 mb-2.5 md:mb-4">
                {[...Array(5)].map((_, idx) => (
                  <i key={idx} className="ri-star-fill text-forest-500 text-xs" />
                ))}
              </div>
              <p className="text-charcoal-600 text-xs md:text-sm leading-relaxed mb-4 md:mb-6">
                &ldquo;{t.text}&rdquo;
              </p>
              <div className="flex items-center gap-3 pt-3 md:pt-4 border-t border-charcoal-100">
                <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-forest-600 text-white text-xs font-semibold flex-shrink-0">
                  {t.initials}
                </div>
                <div>
                  <p className="text-charcoal-900 text-xs md:text-sm font-semibold">{t.name}</p>
                  <p className="text-charcoal-400 text-xs mt-0.5">
                    {t.role}
                    {t.company !== "Independent" && ` · ${t.company}`}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Show more / less button — mobile only */}
        <div className="md:hidden flex justify-center mt-5">
          <button
            onClick={() => setShowAll(!showAll)}
            className="flex items-center gap-2 text-forest-600 text-xs font-semibold border border-forest-300 rounded-md px-5 py-2.5 hover:bg-forest-50 transition-colors cursor-pointer"
          >
            {showAll ? (
              <><i className="ri-arrow-up-s-line" /> Show Less</>
            ) : (
              <><i className="ri-arrow-down-s-line" /> Show {testimonials.length - 3} More Reviews</>
            )}
          </button>
        </div>

        {/* Logos row */}
        <div
          className={`mt-10 md:mt-16 pt-7 md:pt-12 border-t border-charcoal-200 transition-all duration-700 delay-500 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <p className="text-center text-charcoal-400 text-xs tracking-widest uppercase font-medium mb-4 md:mb-8">
            Companies We&apos;ve Worked With
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 md:gap-x-12 gap-y-2.5 md:gap-y-4">
            {[
              "Mitchell & Co. Ltd",
              "Hartwell Construction",
              "Okonkwo Logistics Group",
              "Crane & Webb Legal",
              "Ferndale Media Group",
              "Brightstone Holdings",
            ].map((name) => (
              <span
                key={name}
                className="text-charcoal-300 font-medium text-xs tracking-wide hover:text-charcoal-500 transition-colors cursor-default"
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
